import type {
  BusinessCenterDocumentRecord,
  BusinessCenterRoleKey,
  BusinessCenterTaskRecord,
  ClientPortalPayload,
  ClientPortalWorkspaceKey,
} from "./types";
import {
  buildBusinessCenterPermissionContext,
  canAccessBilling,
  canAccessReports,
  canView,
} from "./businessCenterPermissions";
import { emptyBusinessCenterUnifiedDataModel, type BusinessCenterUnifiedDataModel } from "./businessCenterDataModel";

export type ClientPortalNavKey = "dashboard" | "projects" | "tasks" | "documents" | "reports" | "billing" | "messages";

export interface ClientPortalNavItem {
  key: ClientPortalNavKey;
  label: string;
  href: string;
  description: string;
  available: boolean;
}

export interface ClientPortalWorkspaceAction {
  label: string;
  href: string;
  description: string;
}

export interface ClientPortalWorkspaceTheme {
  tone: "dark" | "light";
  shellClass: string;
  heroClass: string;
  cardClass: string;
  sectionClass: string;
  accentClass: string;
  mutedClass: string;
  headingClass: string;
}

interface WorkspaceNavigationCopy {
  label: string;
  description: string;
  href: string;
  requiresBilling?: boolean;
  requiresReports?: boolean;
}

interface WorkspaceScopeCardCopy {
  label: string;
  note: string;
  source: "projects" | "tasks" | "documents" | "payments" | "proposals" | "agreements";
}

export interface ClientPortalWorkspaceDefinition {
  key: ClientPortalWorkspaceKey;
  label: string;
  shortLabel: string;
  eyebrow: string;
  title: string;
  description: string;
  lead: string;
  primaryAction: ClientPortalWorkspaceAction;
  secondaryActions: ClientPortalWorkspaceAction[];
  navigation: Record<ClientPortalNavKey, WorkspaceNavigationCopy>;
  scopeCards: WorkspaceScopeCardCopy[];
  tasksSection: {
    eyebrow: string;
    title: string;
    description: string;
    emptyTitle: string;
    emptyMessage: string;
  };
  documentsSection: {
    eyebrow: string;
    title: string;
    description: string;
    emptyTitle: string;
    emptyMessage: string;
  };
  projectsSection: {
    eyebrow: string;
    title: string;
    description: string;
    emptyTitle: string;
    emptyMessage: string;
  };
  billingSection: {
    eyebrow: string;
    title: string;
    description: string;
    emptyTitle: string;
    emptyMessage: string;
  };
  messagesSection: {
    eyebrow: string;
    title: string;
    description: string;
    placeholderLabel: string;
    placeholderMessage: string;
  };
  experienceSection: {
    eyebrow: string;
    title: string;
    description: string;
    modules: ClientPortalWorkspaceAction[];
  };
  theme: ClientPortalWorkspaceTheme;
}

export interface ClientPortalScopeSnapshot {
  companyId: string;
  role: BusinessCenterRoleKey;
  canAccessBilling: boolean;
  canAccessReports: boolean;
  canViewPortal: boolean;
  visibleProjects: ClientPortalPayload["projects"];
  visibleTasks: BusinessCenterTaskRecord[];
  visibleDocuments: BusinessCenterDocumentRecord[];
  visiblePayments: ClientPortalPayload["payments"];
  visibleActivity: ClientPortalPayload["activity"];
}

const workspaceDefinitions: Record<ClientPortalWorkspaceKey, ClientPortalWorkspaceDefinition> = {
  kreatethedate: {
    key: "kreatethedate",
    label: "Kreatethedate",
    shortLabel: "Kreatethedate",
    eyebrow: "Luxury event journey",
    title: "Luxury event client portal",
    description: "RSVP Hub, Memory Hub, Archive Hub, and Memory Media Vault records stay inside one shared client-owned workspace.",
    lead: "Each record is filtered by company before it reaches the UI, so the client only sees the experience that belongs to them.",
    primaryAction: {
      label: "Open Memory Media Vault",
      href: "/client/dashboard",
      description: "Open the event delivery workspace and review gallery progress.",
    },
    secondaryActions: [
      { label: "RSVP Hub", href: "#tasks", description: "Capture attendance and planning updates." },
      { label: "Memory Hub", href: "#documents", description: "Review memories, uploads, and delivery assets." },
      { label: "Archive Hub", href: "#reports", description: "Review legacy planning and archive-ready records." },
    ],
    navigation: {
      dashboard: { label: "Dashboard", href: "#dashboard", description: "Overview and status summary." },
      projects: { label: "Projects", href: "#projects", description: "Event delivery and progress records." },
      tasks: { label: "RSVP Hub", href: "#tasks", description: "Guest follow-up and planning items." },
      documents: { label: "Memory Hub", href: "#documents", description: "Approved files, notes, and uploads." },
      reports: { label: "Archive Hub", href: "#reports", description: "Legacy planning and archive-ready summaries.", requiresReports: true },
      billing: { label: "Memory Media Vault", href: "#billing", description: "Invoices, payments, and delivery billing.", requiresBilling: true },
      messages: { label: "Messages", href: "#messages", description: "Shared inbox placeholder and activity." },
    },
    scopeCards: [
      { label: "Projects", source: "projects", note: "Event planning and delivery records." },
      { label: "RSVP Items", source: "tasks", note: "Attendance and follow-up items." },
      { label: "Memory Files", source: "documents", note: "Scoped uploads, notes, and deliverables." },
      { label: "Invoices", source: "payments", note: "Billing history and payment tracking." },
    ],
    tasksSection: {
      eyebrow: "RSVP and follow-up",
      title: "RSVP Hub",
      description: "Guest follow-up items, planning touchpoints, and event-ready tasks are grouped here.",
      emptyTitle: "No RSVP items visible yet",
      emptyMessage: "RSVP follow-ups will appear here once the shared portal sync is connected to event work items.",
    },
    documentsSection: {
      eyebrow: "Memory Hub",
      title: "Memory Hub",
      description: "Approved files, memories, and event assets remain visible inside the client-owned portal.",
      emptyTitle: "No memory files visible yet",
      emptyMessage: "Memory Hub files will appear here once event uploads are connected to the portal scope.",
    },
    projectsSection: {
      eyebrow: "Event delivery",
      title: "Projects",
      description: "Planning, delivery, and archive-related project records stay in one workspace.",
      emptyTitle: "No projects visible yet",
      emptyMessage: "Projects will appear here once event delivery work is linked to this company.",
    },
    billingSection: {
      eyebrow: "Memory Media Vault",
      title: "Invoices",
      description: "Read-only billing and invoice history for the event workspace.",
      emptyTitle: "No billing records visible yet",
      emptyMessage: "Invoices and payment history will appear here once billing records are scope-linked.",
    },
    messagesSection: {
      eyebrow: "Support and activity",
      title: "Messages",
      description: "Activity, reminders, and support updates stay in the same client-facing shell.",
      placeholderLabel: "Messages placeholder",
      placeholderMessage: "A future shared inbox will live here after portal messaging is connected.",
    },
    experienceSection: {
      eyebrow: "Client experience",
      title: "Brand experience",
      description: "RSVP, Memory, Archive, and Vault experiences remain brand-scoped and client-owned.",
      modules: [
        { label: "RSVP Hub", href: "#tasks", description: "Attendance and guest details." },
        { label: "Memory Hub", href: "#documents", description: "Favorite memories and highlights." },
        { label: "Archive Hub", href: "#reports", description: "Legacy and archive planning." },
        { label: "Memory Media Vault", href: "/client/dashboard", description: "Uploads, approvals, and gallery review." },
      ],
    },
    theme: {
      tone: "light",
      shellClass: "min-h-screen bg-[linear-gradient(180deg,#FFF9F0_0%,#FFF4E6_100%)] text-[#332a1f]",
      heroClass: "border-[#FF6A2B]/20 bg-white text-[#332a1f]",
      cardClass: "border-[#FF6A2B]/18 bg-white text-[#332a1f]",
      sectionClass: "border-[#FF6A2B]/18 bg-white",
      accentClass: "text-[#D94A16]",
      mutedClass: "text-[#5c5246]",
      headingClass: "text-[#071A2F]",
    },
  },
  truevance_connect: {
    key: "truevance_connect",
    label: "Truevance Connect",
    shortLabel: "Truevance",
    eyebrow: "Consulting operations journey",
    title: "Consulting client operations portal",
    description: "Assessments, recommendations, projects, documents, and invoices are presented through one shared client workspace.",
    lead: "The same portal engine is reused for every workspace, but the navigation, labels, and hero content adapt to the active tenant.",
    primaryAction: {
      label: "Open Client Overview",
      href: "#dashboard",
      description: "Open the consulting portal overview and current status.",
    },
    secondaryActions: [
      { label: "Assessments", href: "#tasks", description: "Review discovery and diagnostic progress." },
      { label: "Recommendations", href: "#reports", description: "Review scope, guidance, and planning." },
      { label: "Projects", href: "#projects", description: "Track delivery milestones and support items." },
    ],
    navigation: {
      dashboard: { label: "Dashboard", href: "#dashboard", description: "Overview and status summary." },
      projects: { label: "Projects", href: "#projects", description: "Delivery and progress records." },
      tasks: { label: "Assessments", href: "#tasks", description: "Discovery, intake, and action items." },
      documents: { label: "Documents", href: "#documents", description: "Files, deliverables, and records." },
      reports: { label: "Recommendations", href: "#reports", description: "Guidance, summaries, and executive view.", requiresReports: true },
      billing: { label: "Invoices", href: "#billing", description: "Invoice and payment history.", requiresBilling: true },
      messages: { label: "Messages", href: "#messages", description: "Shared inbox placeholder and activity." },
    },
    scopeCards: [
      { label: "Assessments", source: "tasks", note: "Discovery work and assigned actions." },
      { label: "Recommendations", source: "agreements", note: "Guidance, approvals, and next-step records." },
      { label: "Projects", source: "projects", note: "Scoped delivery records and milestones." },
      { label: "Documents", source: "documents", note: "Scope-linked files and deliverables." },
    ],
    tasksSection: {
      eyebrow: "Discovery and intake",
      title: "Assessments",
      description: "Client discovery, diagnostics, and follow-up actions are grouped in one workspace section.",
      emptyTitle: "No assessments visible yet",
      emptyMessage: "Assessments will appear here once discovery records are linked to this company.",
    },
    documentsSection: {
      eyebrow: "Client files",
      title: "Documents",
      description: "Deliverables and reference files stay grouped by company and project scope.",
      emptyTitle: "No documents visible yet",
      emptyMessage: "Documents will appear here once client files are connected to the portal.",
    },
    projectsSection: {
      eyebrow: "Implementation delivery",
      title: "Projects",
      description: "Consulting delivery, milestones, and support records remain in the same shared portal.",
      emptyTitle: "No projects visible yet",
      emptyMessage: "Projects will appear here once delivery records are linked to the company.",
    },
    billingSection: {
      eyebrow: "Invoices and payments",
      title: "Invoices",
      description: "Read-only billing history and payment tracking for the consulting workspace.",
      emptyTitle: "No invoices visible yet",
      emptyMessage: "Invoices and payment history will appear here once billing records are scope-linked.",
    },
    messagesSection: {
      eyebrow: "Support and activity",
      title: "Messages",
      description: "Client updates, reminders, and activity remain in the same portal shell.",
      placeholderLabel: "Messages placeholder",
      placeholderMessage: "A future shared inbox will live here after portal messaging is connected.",
    },
    experienceSection: {
      eyebrow: "Client experience",
      title: "Workspace experience",
      description: "Assessments, recommendations, roadmaps, projects, and support work stay in one client-scoped operating view.",
      modules: [
        { label: "Assessments", href: "#tasks", description: "Discovery and diagnostic progress." },
        { label: "Recommendations", href: "#reports", description: "Executive guidance and planning." },
        { label: "Projects", href: "#projects", description: "Implementation milestones and delivery." },
        { label: "Documents", href: "#documents", description: "Deliverables and working files." },
      ],
    },
    theme: {
      tone: "dark",
      shellClass: "min-h-screen bg-[radial-gradient(circle_at_top,_rgba(34,224,208,.15),_transparent_40%),linear-gradient(180deg,#071A2F_0%,#0A2540_48%,#071A2F_100%)] text-[#F5F7FA]",
      heroClass: "border-[rgba(34,224,208,.18)] bg-[rgba(7,26,47,.72)] text-[#F5F7FA]",
      cardClass: "border-[rgba(34,224,208,.14)] bg-[rgba(255,255,255,.04)] text-[#F5F7FA]",
      sectionClass: "border-[rgba(34,224,208,.16)] bg-[rgba(7,26,47,.76)]",
      accentClass: "text-[#22E0D0]",
      mutedClass: "text-[#E8EEF4]",
      headingClass: "text-[#F5F7FA]",
    },
  },
  ar_recovery: {
    key: "ar_recovery",
    label: "AR Recovery",
    shortLabel: "AR Recovery",
    eyebrow: "Recovery command center",
    title: "Accounts receivable recovery portal",
    description: "Recovery Dashboard, Customer Accounts, Statements, Collection Letters, Promise-to-Pay, and Reports live inside one branded client portal.",
    lead: "The same portal engine now adapts to collections operations, while company scope and role visibility still control what can be seen.",
    primaryAction: {
      label: "Open Recovery Dashboard",
      href: "#dashboard",
      description: "Open the recovery command center for the active company.",
    },
    secondaryActions: [
      { label: "Customer Accounts", href: "#projects", description: "Review account-level recovery records." },
      { label: "Promise-to-Pay", href: "#tasks", description: "Track commitment dates and follow-up items." },
      { label: "Collection Letters", href: "#documents", description: "View letters and recovery correspondence." },
    ],
    navigation: {
      dashboard: { label: "Recovery Dashboard", href: "#dashboard", description: "Open, aging, and activity summary." },
      projects: { label: "Customer Accounts", href: "#projects", description: "Account list and recovery status." },
      tasks: { label: "Promise-to-Pay", href: "#tasks", description: "Promises, follow-ups, and reminders." },
      documents: { label: "Collection Letters", href: "#documents", description: "Letters, statements, and correspondence." },
      reports: { label: "Reports", href: "#reports", description: "Collector and recovery performance.", requiresReports: true },
      billing: { label: "Statements", href: "#billing", description: "Invoices, balances, and payment history.", requiresBilling: true },
      messages: { label: "Messages", href: "#messages", description: "Activity timeline and communication history." },
    },
    scopeCards: [
      { label: "Customer Accounts", source: "projects", note: "Account-level recovery records." },
      { label: "Statements", source: "proposals", note: "Open balance and statement history." },
      { label: "Collection Letters", source: "agreements", note: "Customer notices and correspondence." },
      { label: "Promise-to-Pay", source: "tasks", note: "Commitments, follow-ups, and reminders." },
    ],
    tasksSection: {
      eyebrow: "Promise and follow-up",
      title: "Promise-to-Pay",
      description: "Follow-up reminders, due dates, and promise tracking live in one operational queue.",
      emptyTitle: "No promise-to-pay records visible yet",
      emptyMessage: "Promise-to-pay items will appear here once recovery records are linked to the company.",
    },
    documentsSection: {
      eyebrow: "Collections correspondence",
      title: "Collection Letters",
      description: "Letters, statements, and recovery correspondence stay scoped to the active company.",
      emptyTitle: "No collection letters visible yet",
      emptyMessage: "Collection letters will appear here once recovery correspondence is connected to the portal.",
    },
    projectsSection: {
      eyebrow: "Customer accounts",
      title: "Customer Accounts",
      description: "Recovery records, balances, and account history are organized by customer company.",
      emptyTitle: "No customer accounts visible yet",
      emptyMessage: "Customer account records will appear here once company-scoped recovery data is linked.",
    },
    billingSection: {
      eyebrow: "Statements and invoices",
      title: "Statements",
      description: "Read-only statements, balances, and payment history for the recovery workspace.",
      emptyTitle: "No statements visible yet",
      emptyMessage: "Statements will appear here once recovery billing records are scope-linked.",
    },
    messagesSection: {
      eyebrow: "Activity and communication",
      title: "Activity timeline",
      description: "Calls, emails, notes, and workflow activity remain inside the same workspace.",
      placeholderLabel: "Messages placeholder",
      placeholderMessage: "A future shared inbox will live here after recovery messaging is connected.",
    },
    experienceSection: {
      eyebrow: "Recovery experience",
      title: "Operations view",
      description: "The portal now shifts into a recovery command center without changing the underlying engine.",
      modules: [
        { label: "Recovery Dashboard", href: "#dashboard", description: "Open and aging summary." },
        { label: "Customer Accounts", href: "#projects", description: "Account and balance review." },
        { label: "Statements", href: "#billing", description: "Invoices and statement history." },
        { label: "Promise-to-Pay", href: "#tasks", description: "Follow-up reminders and commitments." },
      ],
    },
    theme: {
      tone: "dark",
      shellClass: "min-h-screen bg-[radial-gradient(circle_at_top,_rgba(220,38,38,.16),_transparent_38%),linear-gradient(180deg,#08111F_0%,#111827_46%,#08111F_100%)] text-[#F5F7FA]",
      heroClass: "border-[rgba(244,114,182,.18)] bg-[rgba(8,17,31,.72)] text-[#F5F7FA]",
      cardClass: "border-[rgba(244,114,182,.14)] bg-[rgba(255,255,255,.04)] text-[#F5F7FA]",
      sectionClass: "border-[rgba(244,114,182,.16)] bg-[rgba(8,17,31,.76)]",
      accentClass: "text-[#F97316]",
      mutedClass: "text-[#E8EEF4]",
      headingClass: "text-[#F5F7FA]",
    },
  },
};

export function resolveClientPortalWorkspaceKey(
  portal: ClientPortalPayload,
  preferredWorkspace?: string | null,
): ClientPortalWorkspaceKey {
  if (preferredWorkspace && preferredWorkspace in workspaceDefinitions) {
    return preferredWorkspace as ClientPortalWorkspaceKey;
  }

  if (portal.portalWorkspace && portal.portalWorkspace in workspaceDefinitions) {
    return portal.portalWorkspace;
  }

  if (portal.brand && portal.brand in workspaceDefinitions) {
    return portal.brand as ClientPortalWorkspaceKey;
  }

  const fingerprint = `${portal.client.clientSlug} ${portal.client.clientName} ${portal.client.organizationName || ""} ${portal.brand || ""}`.toLowerCase();
  if (fingerprint.includes("recovery")) return "ar_recovery";
  if (fingerprint.includes("truevance")) return "truevance_connect";
  return "kreatethedate";
}

export function buildClientPortalWorkspaceDefinition(workspaceKey: ClientPortalWorkspaceKey): ClientPortalWorkspaceDefinition {
  return workspaceDefinitions[workspaceKey];
}

export function buildClientPortalScopeSnapshot(
  portal: ClientPortalPayload,
  model: BusinessCenterUnifiedDataModel = emptyBusinessCenterUnifiedDataModel,
  role: BusinessCenterRoleKey = "client",
): ClientPortalScopeSnapshot {
  const context = buildBusinessCenterPermissionContext(model, role, portal.client.id);
  const portalAllowsBilling = canAccessBilling(context);
  const portalAllowsReports = canAccessReports(context);
  const portalVisible = canView(context, {
    entityType: "portal_access",
    entityId: portal.client.id,
    companyId: portal.client.id,
    portalOnly: true,
  });

  return {
    companyId: portal.client.id,
    role,
    canAccessBilling: portalAllowsBilling,
    canAccessReports: portalAllowsReports,
    canViewPortal: portalVisible,
    visibleProjects: filterByCompanyId(portal.projects, portal.client.id),
    visibleTasks: filterByCompanyId(portal.tasks || [], portal.client.id),
    visibleDocuments: filterByCompanyId(portal.documents || [], portal.client.id),
    visiblePayments: filterByCompanyId(portal.payments, portal.client.id),
    visibleActivity: filterActivityByCompanyId(portal.activity, portal.client.id),
  };
}

export function buildClientPortalNavigation(
  snapshot: ClientPortalScopeSnapshot,
  workspaceKey: ClientPortalWorkspaceKey = "kreatethedate",
): ClientPortalNavItem[] {
  const workspace = workspaceDefinitions[workspaceKey];
  return (Object.entries(workspace.navigation) as Array<[ClientPortalNavKey, WorkspaceNavigationCopy]>).map(([key, nav]) => ({
    key,
    label: nav.label,
    href: nav.href,
    description: nav.description,
    available: key === "reports" ? snapshot.canAccessReports : key === "billing" ? snapshot.canAccessBilling : true,
  }));
}

export function filterByCompanyId<
  T extends { companyId?: string | null; clientId?: string | null; projectId?: string | null },
>(records: T[], companyId: string): T[] {
  return records.filter((record) => {
    if (record.companyId === companyId) return true;
    if (record.clientId === companyId) return true;
    if (record.projectId === companyId) return true;
    return false;
  });
}

export function filterActivityByCompanyId(records: ClientPortalPayload["activity"], companyId: string) {
  return records.filter((record) => {
    if (record.companyId === companyId) return true;
    if (record.clientId === companyId) return true;
    if (record.projectId === companyId) return true;
    return false;
  });
}

export function formatPortalRecordCount(value: number) {
  return new Intl.NumberFormat("en-US").format(value || 0);
}

export function portalSectionToneToClass(tone: "dark" | "light") {
  return tone === "dark" ? "border-[rgba(34,224,208,.16)] bg-[rgba(7,26,47,.76)]" : "border-[#FF6A2B]/18 bg-white";
}

export function portalSectionHeadingToClass(tone: "dark" | "light") {
  return tone === "dark" ? "text-[#F5F7FA]" : "text-[#071A2F]";
}

export function portalSectionMutedToClass(tone: "dark" | "light") {
  return tone === "dark" ? "text-[#E8EEF4]" : "text-[#5c5246]";
}

export function portalAccentToClass(tone: "dark" | "light") {
  return tone === "dark" ? "text-[#22E0D0]" : "text-[#D94A16]";
}

function countPortalScopeSource(
  portal: ClientPortalPayload,
  snapshot: ClientPortalScopeSnapshot,
  source: WorkspaceScopeCardCopy["source"],
) {
  switch (source) {
    case "projects":
      return snapshot.visibleProjects.length;
    case "tasks":
      return snapshot.visibleTasks.length;
    case "documents":
      return snapshot.visibleDocuments.length;
    case "payments":
      return snapshot.visiblePayments.length;
    case "proposals":
      return filterByCompanyId(portal.proposals, snapshot.companyId).length;
    case "agreements":
      return filterByCompanyId(portal.agreements, snapshot.companyId).length;
    default:
      return 0;
  }
}

export function buildPortalRecordScopeCards(
  portal: ClientPortalPayload,
  snapshot: ClientPortalScopeSnapshot,
  workspaceKey: ClientPortalWorkspaceKey = "kreatethedate",
) {
  return workspaceDefinitions[workspaceKey].scopeCards.map((card) => ({
    label: card.label,
    value: countPortalScopeSource(portal, snapshot, card.source),
    note: card.note,
  }));
}

export function buildPortalScopeExample(portal: ClientPortalPayload, snapshot: ClientPortalScopeSnapshot) {
  return {
    companyId: snapshot.companyId,
    companyName: portal.client.clientName,
    visibleProjects: snapshot.visibleProjects.slice(0, 3),
    visibleTasks: snapshot.visibleTasks.slice(0, 3),
    visibleDocuments: snapshot.visibleDocuments.slice(0, 3),
    visiblePayments: snapshot.visiblePayments.slice(0, 3),
  };
}
